import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowSquareOutIcon, CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { useAdminStore } from "../../stores/admin-store";
import {
  AdminDeficiency,
  DeficiencySeverity,
  DeficiencyStatus,
  createPromptRevisionForDeficiency,
  getAdminDeficiencies,
  getDeficiencyProjects,
  getDetectionRules,
  getGenerationTaskStatus,
  rescanDeficiencies,
  saveLocalFix,
  updateDeficiency,
  updateDetectionRules
} from "../../shared/services/deficiencies";
import { getRoutePromptsRevision } from "../../shared/const/router";
import {
  CASE_BRIEF_FEATURE_KEY,
  SEVERITIES,
  SEVERITY_ORDER,
  SEVERITY_STYLES,
  STATUS_LABELS,
  STATUS_STYLES,
  TYPE_LABELS,
  caseBriefUrl,
  chapterUrl
} from "../../shared/components/DeficiencyDialog/deficiencyDisplay";

const POLL_INTERVAL_MS = 5000;

function usePersistedFilter(key: string, initial: string) {
  const [value, setValue] = useState<string>(() => {
    try {
      return localStorage.getItem(key) ?? initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore storage failures
    }
  }, [key, value]);
  return [value, setValue] as const;
}

export const DeficienciesPage: FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAdminStore();
  const isSystemAdmin = user?.role === "systemAdmin";

  const [projectFilter, setProjectFilter] = usePersistedFilter("deficiencies.filter.project", "all");
  const [orgFilter, setOrgFilter] = usePersistedFilter("deficiencies.filter.org", "all");
  const [typeFilter, setTypeFilter] = usePersistedFilter("deficiencies.filter.type", "all");
  const [severityFilter, setSeverityFilter] = usePersistedFilter("deficiencies.filter.severity", "all");
  const [sourceFilter, setSourceFilter] = usePersistedFilter("deficiencies.filter.source", "all");
  const [subjectFilter, setSubjectFilter] = usePersistedFilter("deficiencies.filter.subject", "all");
  const [statusFilter, setStatusFilter] = usePersistedFilter("deficiencies.filter.status", "open");
  const [scanningProjectId, setScanningProjectId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: deficiencies = [], isLoading, error } = useQuery<AdminDeficiency[]>({
    queryKey: ["admin-deficiencies"],
    queryFn: () => getAdminDeficiencies(),
    enabled: isSystemAdmin
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["deficiency-projects"],
    queryFn: getDeficiencyProjects,
    enabled: isSystemAdmin
  });

  const patch = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { severity?: DeficiencySeverity; status?: DeficiencyStatus } }) =>
      updateDeficiency(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ["admin-deficiencies"] });
      const prev = queryClient.getQueryData<AdminDeficiency[]>(["admin-deficiencies"]);
      queryClient.setQueryData<AdminDeficiency[]>(["admin-deficiencies"], (old) =>
        (old ?? []).map((d) =>
          d.id === id ? { ...d, ...data, severityOverridden: data.severity ? true : d.severityOverridden } : d
        )
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["admin-deficiencies"], ctx.prev);
      toast.error("Update failed.");
    }
  });

  const orgOptions = useMemo(() => {
    const names = new Set<string>();
    projects.forEach((p) => {
      if (p.organizationName) names.add(p.organizationName);
    });
    return Array.from(names).sort();
  }, [projects]);

  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => orgFilter === "all" || p.organizationName === orgFilter)
        .map((p) => ({
          id: p.id,
          label:
            p.versionDisplayName && p.versionDisplayName !== "Initial" ? `${p.name} · ${p.versionDisplayName}` : p.name
        })),
    [projects, orgFilter]
  );

  const presentTypes = useMemo(() => {
    const set = new Set<string>();
    deficiencies.forEach((d) => set.add(d.type));
    return Array.from(set);
  }, [deficiencies]);

  const filtered = useMemo(() => {
    return deficiencies
      .filter((d) => orgFilter === "all" || d.organizationName === orgFilter)
      .filter((d) => projectFilter === "all" || d.projectId === Number(projectFilter))
      .filter((d) => typeFilter === "all" || d.type === typeFilter)
      .filter((d) => severityFilter === "all" || d.severity === severityFilter)
      .filter((d) => sourceFilter === "all" || d.detectedBy === sourceFilter)
      .filter((d) => subjectFilter === "all" || d.subject === subjectFilter)
      .filter((d) => statusFilter === "all" || d.status === statusFilter)
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }, [deficiencies, orgFilter, projectFilter, typeFilter, severityFilter, sourceFilter, subjectFilter, statusFilter]);

  const lastScannedAt = useMemo(() => {
    if (projectFilter === "all") return null;
    return deficiencies.find((d) => d.projectId === Number(projectFilter))?.lastScannedAt ?? null;
  }, [deficiencies, projectFilter]);

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const handleRescan = async () => {
    if (projectFilter === "all") return;
    const projectId = Number(projectFilter);
    setScanningProjectId(projectId);
    try {
      const { generationId } = await rescanDeficiencies(projectId);
      toast.success("Re-scan started. Findings will refresh when it completes.");

      const poll = async () => {
        try {
          const { status, result } = await getGenerationTaskStatus(generationId);
          if (status === "ready" || status === "processed") {
            await queryClient.invalidateQueries({ queryKey: ["admin-deficiencies"] });
            setScanningProjectId(null);
            stopPolling();
            let skipped = false;
            try {
              skipped = result ? JSON.parse(result)?.skipped === true : false;
            } catch {
              skipped = false;
            }
            toast.success(skipped ? "No changes since the last scan — findings unchanged." : "Re-scan complete.");
          } else if (status === "error" || status === "errorSeen" || status === "cancelled") {
            setScanningProjectId(null);
            stopPolling();
            toast.error("Re-scan failed.");
          } else {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        } catch {
          setScanningProjectId(null);
          stopPolling();
          toast.error("Error checking re-scan status.");
        }
      };
      pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch {
      setScanningProjectId(null);
      toast.error("Failed to start re-scan.");
    }
  };

  if (!isSystemAdmin) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">Access denied — systemAdmin role required.</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading deficiencies...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">Error loading deficiencies</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8" data-testid="deficiencies-page">
      <div className="flex justify-between items-center mb-6 pr-6">
        <FigmaTypography component="h1" variant="heading-1" data-testid="deficiencies-heading">
          Deficiencies
        </FigmaTypography>
        <div className="flex items-center gap-3">
          {lastScannedAt && projectFilter !== "all" && (
            <FigmaTypography component="span" variant="body-3" className="text-gray-400">
              Last scanned:{" "}
              {new Date(lastScannedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </FigmaTypography>
          )}
          <AppButton
            appearance="contained"
            onClick={handleRescan}
            disabled={projectFilter === "all" || scanningProjectId !== null}
            data-testid="rescan-button"
          >
            {scanningProjectId !== null ? "Scanning..." : "Re-scan project"}
          </AppButton>
        </div>
      </div>

      <DetectionRulesPanel />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FilterSelect
          value={orgFilter}
          onValueChange={(v) => {
            setOrgFilter(v);
            setProjectFilter("all");
          }}
          placeholder="All organizations"
          width="w-56"
          options={[
            { value: "all", label: "All organizations" },
            ...orgOptions.map((name) => ({ value: name, label: name }))
          ]}
        />
        <ProjectCombobox
          value={projectFilter}
          onChange={setProjectFilter}
          options={[
            { value: "all", label: "All projects" },
            ...projectOptions.map((p) => ({ value: String(p.id), label: p.label }))
          ]}
        />
        <FilterSelect
          value={statusFilter}
          onValueChange={setStatusFilter}
          placeholder="Open"
          options={[
            { value: "open", label: "Open" },
            { value: "in_review", label: "In review" },
            { value: "cured", label: "Cured" },
            { value: "dismissed", label: "Dismissed" },
            { value: "stale", label: "Stale" },
            { value: "all", label: "All statuses" }
          ]}
        />
        <FilterSelect
          value={typeFilter}
          onValueChange={setTypeFilter}
          placeholder="All types"
          options={[
            { value: "all", label: "All types" },
            ...presentTypes.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }))
          ]}
        />
        <FilterSelect
          value={severityFilter}
          onValueChange={setSeverityFilter}
          placeholder="Any severity"
          options={[
            { value: "all", label: "Any severity" },
            { value: "critical", label: "Critical" },
            { value: "major", label: "Major" },
            { value: "minor", label: "Minor" }
          ]}
        />
        <FilterSelect
          value={sourceFilter}
          onValueChange={setSourceFilter}
          placeholder="Any source"
          options={[
            { value: "all", label: "Any source" },
            { value: "ai", label: "AI scan" },
            { value: "human", label: "Human flag" }
          ]}
        />
        <FilterSelect
          value={subjectFilter}
          onValueChange={setSubjectFilter}
          placeholder="Anywhere"
          options={[
            { value: "all", label: "Anywhere" },
            { value: "memo_chapter", label: "Memo" },
            { value: "case_brief", label: "Case brief" }
          ]}
        />
        <span className="text-[14px] text-gray-500 ml-auto">
          {filtered.length} of {deficiencies.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500">No deficiencies found</div>
        </div>
      ) : (
        <div className="flex flex-col rounded-lg border bg-white shadow-sm divide-y">
          {filtered.map((d) => (
            <DeficiencyRow
              key={d.id}
              deficiency={d}
              isExpanded={expandedId === d.id}
              onToggle={() => setExpandedId((cur) => (cur === d.id ? null : d.id))}
              onSeverityChange={(severity) => patch.mutate({ id: d.id, data: { severity } })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
  width = "w-48"
}: {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  width?: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={`${width} h-9 text-[14px] bg-white`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-[14px]">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ProjectCombobox({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-64 items-center justify-between gap-2 rounded-md border bg-white px-3 text-left text-[14px]"
        >
          <span className="truncate">{selected?.label ?? "All projects"}</span>
          <CaretDownIcon size={14} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search projects..." />
          <CommandList>
            <CommandEmpty>No projects found.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="text-[14px]"
                >
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DetectionRulesPanel() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const { data } = useQuery({ queryKey: ["detection-rules"], queryFn: getDetectionRules });

  useEffect(() => {
    if (data && draft === null) setDraft(data.rules);
  }, [data, draft]);

  const save = useMutation({
    mutationFn: (rules: string | null) => updateDetectionRules(rules),
    onSuccess: (res) => {
      setDraft(res.rules);
      queryClient.setQueryData(["detection-rules"], res);
      toast.success("Detection rules saved. Re-scan a project to apply.");
    },
    onError: () => toast.error("Failed to save rules.")
  });

  const dirty = data != null && draft != null && draft !== data.rules;

  return (
    <div className="mb-4 rounded-lg border bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? <CaretDownIcon size={14} /> : <CaretRightIcon size={14} />}
        <span className="text-[14px] font-medium text-gray-700">Detection rules</span>
        <FigmaTypography component="span" variant="body-3" className="text-gray-400">{data?.isCustom ? "Custom" : "Default"}</FigmaTypography>
        {data?.updatedAt && (
          <FigmaTypography component="span" variant="body-3" className="text-gray-400">
            · edited {new Date(data.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </FigmaTypography>
        )}
      </button>

      {open && (
        <div className="border-t px-4 py-3 flex flex-col gap-3">
          <FigmaTypography variant="body-3" className="text-gray-500">
            The semantic rules the AI uses to decide what counts as a deficiency. Editing these does not change existing
            findings — save, then re-scan a project to apply.
          </FigmaTypography>
          <textarea
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            className="min-h-[280px] w-full resize-y rounded-md border border-gray-200 p-3 font-mono text-[12px] leading-relaxed text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-300"
          />
          <div className="flex items-center gap-2">
            <AppButton
              appearance="contained"
              onClick={() => draft != null && save.mutate(draft)}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? "Saving..." : "Save rules"}
            </AppButton>
            <AppButton
              appearance="outlined"
              color="zinc150"
              onClick={() => save.mutate(null)}
              disabled={save.isPending || !data?.isCustom}
            >
              Reset to default
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
}

function DeficiencyRow({
  deficiency: d,
  isExpanded,
  onToggle,
  onSeverityChange
}: {
  deficiency: AdminDeficiency;
  isExpanded: boolean;
  onToggle: () => void;
  onSeverityChange: (severity: DeficiencySeverity) => void;
}) {
  const date = new Date(d.createdAt).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
  const typeText = d.type === "other" && d.typeLabel ? d.typeLabel : TYPE_LABELS[d.type] ?? d.type;

  return (
    <div data-testid={`deficiency-row-${d.id}`}>
      <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50" onClick={onToggle}>
        <span className="text-gray-300">{isExpanded ? <CaretDownIcon size={13} /> : <CaretRightIcon size={13} />}</span>

        <div onClick={(e) => e.stopPropagation()}>
          <SeverityDropdown severity={d.severity} overridden={d.severityOverridden} onChange={onSeverityChange} />
        </div>

        <span className="text-[14px] font-medium text-gray-700 shrink-0">{typeText}</span>
        <FigmaTypography component="span" variant="body-3" className="text-gray-400 shrink-0">{d.detectedBy === "ai" ? `AI · #${d.scanRound}` : "Human"}</FigmaTypography>
        <span className="text-[14px] text-gray-500 truncate flex-1">{d.description}</span>

        {d.status !== "open" && (
          <FigmaTypography component="span" variant="body-3" className={`inline-flex shrink-0 items-center px-2 py-0.5 rounded border ${STATUS_STYLES[d.status]}`}>
            {STATUS_LABELS[d.status]}
          </FigmaTypography>
        )}
        {d.subject === "case_brief" ? (
          <FigmaTypography
            component="span"
            variant="body-3"
            className="text-purple-700 bg-purple-50 px-2 py-0.5 rounded shrink-0 max-w-[200px] truncate"
          >
            Case brief{d.caseBriefName ? ` · ${d.caseBriefName}` : ""}
          </FigmaTypography>
        ) : (
          <FigmaTypography component="span" variant="body-3" className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded shrink-0 max-w-[200px] truncate">
            {d.projectName}
            {d.projectVersion && d.projectVersion !== "Initial" ? ` · ${d.projectVersion}` : ""}
          </FigmaTypography>
        )}
        <FigmaTypography component="span" variant="body-3" className="text-gray-400 shrink-0 w-28 text-right">{date}</FigmaTypography>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-1 pl-10 flex flex-col gap-3 bg-gray-50/50">
          <div className="flex flex-wrap items-center gap-2 text-[12px] leading-[18px] text-gray-500">
            {d.organizationName && <span className="text-gray-400">{d.organizationName}</span>}
            <span>·</span>
            <span>
              {d.detectedBy === "ai"
                ? `Detected by AI scan #${d.scanRound}`
                : `Flagged by ${d.detectedByUserName ?? "user"}${d.detectedByUserEmail ? ` (${d.detectedByUserEmail})` : ""}`}
            </span>
            {d.reviewedByUserName && (
              <>
                <span>·</span>
                <span>Reviewed by {d.reviewedByUserName}</span>
              </>
            )}
          </div>

          <p className="text-[14px] text-gray-800 leading-relaxed">{d.description}</p>

          {d.rootCause && (
            <div className="rounded-md bg-white border border-gray-200 px-3 py-2">
              <div className="text-[12px] uppercase tracking-wide text-gray-400 mb-1">Root cause</div>
              <p className="text-[14px] text-gray-600 leading-relaxed">{d.rootCause}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {d.subject === "case_brief" ? (
              <QuoteBlock
                label={d.caseBriefName ? `Case brief · ${d.caseBriefName}` : "Case brief"}
                id={d.caseBriefId}
                href={d.caseBriefId ? caseBriefUrl(d.caseBriefId) : null}
                quote={d.chapterAQuote}
              />
            ) : (
              <>
                <QuoteBlock
                  label={d.chapterAName ?? ""}
                  id={d.chapterAId}
                  href={d.projectId && d.chapterAId ? chapterUrl(d.projectId, d.chapterAId) : null}
                  quote={d.chapterAQuote}
                />
                {d.chapterBId && d.chapterBQuote && (
                  <QuoteBlock
                    label={d.chapterBName ?? ""}
                    id={d.chapterBId}
                    href={d.projectId ? chapterUrl(d.projectId, d.chapterBId) : null}
                    quote={d.chapterBQuote}
                  />
                )}
              </>
            )}
          </div>

          {d.attachmentUrl && (
            <a
              href={d.attachmentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 self-start rounded-md border border-gray-200 px-2.5 py-1.5 text-[14px] text-blue-600 hover:bg-gray-50"
            >
              <span aria-hidden>📎</span>
              {d.attachmentName ?? "View attachment"}
            </a>
          )}

          {d.subject === "case_brief" ? (
            <PromptRevisionCure deficiency={d} />
          ) : (
            <LocalFixEditor deficiencyId={d.id} initial={d.proposedFix} initialBy={d.proposedFixBy} fixNeeds={d.fixNeeds} />
          )}
        </div>
      )}
    </div>
  );
}

function SeverityDropdown({
  severity,
  overridden,
  onChange
}: {
  severity: DeficiencySeverity;
  overridden: boolean;
  onChange: (severity: DeficiencySeverity) => void;
}) {
  return (
    <Select value={severity} onValueChange={(v) => onChange(v as DeficiencySeverity)}>
      <SelectTrigger
        className={`h-6 w-auto gap-1 rounded-full border px-2 py-0 text-[12px] leading-[18px] font-medium ${SEVERITY_STYLES[severity]}`}
      >
        <span className="capitalize">{severity}</span>
        {overridden && <span title="Severity set by reviewer">✎</span>}
      </SelectTrigger>
      <SelectContent>
        {SEVERITIES.map((s) => (
          <SelectItem key={s} value={s} className="text-[14px] capitalize">
            {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function LocalFixEditor({
  deficiencyId,
  initial,
  initialBy,
  fixNeeds
}: {
  deficiencyId: number;
  initial: string | null;
  initialBy: "ai" | "human" | null;
  fixNeeds: string | null;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(initial ?? "");

  const save = useMutation({
    mutationFn: () => saveLocalFix(deficiencyId, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-deficiencies"] });
      toast.success("Proposed fix saved.");
    },
    onError: () => toast.error("Failed to save proposed fix.")
  });

  const dirty = draft.trim() !== (initial ?? "").trim();

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-[12px] uppercase tracking-wide text-gray-400">Proposed local fix</span>
        {initial && initialBy === "ai" && (
          <span className="text-[10px] rounded bg-blue-50 text-blue-600 px-1.5 py-0.5">AI suggestion</span>
        )}
        {initial && initialBy === "human" && (
          <span className="text-[10px] rounded bg-gray-100 text-gray-500 px-1.5 py-0.5">Reviewer</span>
        )}
      </div>
      {!initial && fixNeeds && (
        <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[12px] text-amber-700">
          No auto-fix yet — needs: {fixNeeds}
        </p>
      )}
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Suggest a corrected version of the flagged text…"
        className="min-h-[72px] w-full resize-y rounded-md border border-gray-200 p-2 text-[14px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-300"
      />
      <div className="flex items-center gap-2">
        <AppButton
          appearance="contained"
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || (!dirty && initialBy !== "ai")}
        >
          {save.isPending ? "Saving..." : dirty ? "Save fix" : initialBy === "ai" ? "Accept suggestion" : "Save fix"}
        </AppButton>
        {initial && !dirty && initialBy === "human" && <FigmaTypography component="span" variant="body-3" className="text-green-600">Saved</FigmaTypography>}
      </div>
    </div>
  );
}

function QuoteBlock({
  label,
  id,
  href,
  quote
}: {
  label: string;
  id: number | null;
  href: string | null;
  quote: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[12px] text-gray-400 font-medium flex items-center gap-1.5">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="hover:text-blue-600 hover:underline transition-colors flex items-center gap-1"
          >
            {label}
            <ArrowSquareOutIcon size={11} className="opacity-50" />
          </a>
        ) : (
          <span>{label}</span>
        )}
        {id !== null && <span className="text-[10px] text-gray-300 font-normal">#{id}</span>}
      </div>
      <blockquote className="border-l-2 border-gray-200 pl-3">
        <p className="text-[14px] text-gray-600 italic leading-relaxed">{quote}</p>
      </blockquote>
    </div>
  );
}

function PromptRevisionCure({ deficiency: d }: { deficiency: AdminDeficiency }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createRevision = useMutation({
    mutationFn: () => createPromptRevisionForDeficiency(CASE_BRIEF_FEATURE_KEY, d.id),
    onSuccess: ({ revisionId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-prompt-revisions", CASE_BRIEF_FEATURE_KEY] });
      navigate(getRoutePromptsRevision(CASE_BRIEF_FEATURE_KEY, revisionId));
    },
    onError: () => toast.error("Failed to create a draft revision.")
  });

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
      <span className="text-[12px] uppercase tracking-wide text-gray-400">Cure</span>
      {d.curedByRevisionId ? (
        <FigmaTypography variant="body-3" className="text-gray-500">
          Cured by prompt revision{" "}
          <button
            type="button"
            onClick={() => navigate(getRoutePromptsRevision(CASE_BRIEF_FEATURE_KEY, d.curedByRevisionId as number))}
            className="text-blue-600 hover:underline"
          >
            #{d.curedByRevisionId}
          </button>
          .
        </FigmaTypography>
      ) : (
        <>
          <FigmaTypography variant="body-3" className="text-gray-500">
            Case brief deficiencies are fixed in the prompts, not in the text. Start a draft revision to edit the
            prompts that produced this brief — activating it marks this deficiency cured.
          </FigmaTypography>
          <AppButton
            appearance="outlined"
            size="sm"
            className="self-start"
            onClick={() => createRevision.mutate()}
            isLoading={createRevision.isPending}
            loadingText="Creating..."
          >
            Create prompt revision
          </AppButton>
        </>
      )}
    </div>
  );
}
